import React, { useEffect, useState } from "react";
import { api, apiErr, fmt } from "@/lib/api";
import { Button, Input, Label, Card, Modal } from "@/components/kit";
import { Plus, Pencil, Trash2, Truck, Package } from "lucide-react";
import { toast } from "sonner";

function CrudBlock({ kind }) {
  const isMachine = kind === "machines";
  const endpoint = isMachine ? "machines" : "materials";
  const empty = isMachine ? { name: "", hourly_rate: 0, unit: "h" } : { name: "", unit: "st", unit_cost: 0 };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    const payload = isMachine
      ? { name: form.name, hourly_rate: Number(form.hourly_rate), unit: form.unit || "h" }
      : { name: form.name, unit: form.unit || "st", unit_cost: Number(form.unit_cost) };
    try {
      if (editId) await api.put(`/${endpoint}/${editId}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success("Sparat");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const edit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Ta bort?")) return; await api.delete(`/${endpoint}/${id}`); load(); };

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
        <div className="flex items-center gap-2">
          {isMachine ? <Truck size={16} className="text-sky-400" /> : <Package size={16} className="text-violet-400" />}
          <h2 className="font-display text-base font-bold text-white">{isMachine ? "Maskiner" : "Material"}</h2>
        </div>
        <Button variant="outline" className="px-3 py-1.5 text-xs" data-testid={`add-${endpoint}-button`} onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}>
          <Plus size={14} /> Ny
        </Button>
      </div>
      <div className="divide-y divide-slate-800/60">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between px-5 py-3" data-testid={`${endpoint}-row-${it.id}`}>
            <div>
              <div className="font-medium text-white">{it.name}</div>
              <div className="text-xs text-slate-500">Enhet: {it.unit || (isMachine ? "h" : "st")}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" className="px-2 py-1.5" data-testid={`edit-${endpoint}-${it.id}`} onClick={() => edit(it)}><Pencil size={13} /></Button>
              <Button variant="ghost" className="px-2 py-1.5 text-red-400" data-testid={`delete-${endpoint}-${it.id}`} onClick={() => del(it.id)}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">Inget tillagt ännu.</div>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Redigera" : isMachine ? "Ny maskin" : "Nytt material"}>
        <form onSubmit={save} className="space-y-4">
          <div><Label>Namn</Label><Input data-testid={`${endpoint}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><Label>Enhet {isMachine ? "(t.ex. h)" : "(t.ex. ton, st, m³)"}</Label><Input data-testid={`${endpoint}-unit-input`} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
          <p className="text-xs text-slate-500">Pris sätts på sidan <span className="text-amber-400">Priser</span>.</p>
          <Button data-testid={`save-${endpoint}-button`} type="submit" className="w-full">Spara</Button>
        </form>
      </Modal>
    </Card>
  );
}

export default function Resources() {
  return (
    <div className="rise">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-amber-500">Hantering</p>
        <h1 className="font-display text-3xl font-extrabold text-white">Maskiner & Material</h1>
        <p className="mt-1 text-sm text-slate-400">Prislistor som anställda väljer från i tidrapporten.</p>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CrudBlock kind="machines" />
        <CrudBlock kind="materials" />
      </div>
    </div>
  );
}
